import { fromPairs, groupBy, keyBy, union } from 'lodash';
import { CompanyId } from 'api/models';
import { MemberType, User, UserStatus } from 'api/models/core-types';
import {
  Empty,
  Loaded,
  RefreshableContent,
  RefreshableEditableContent,
} from 'stores/core';

const MEMBER_TYPES = [MemberType.Native, MemberType.External];

export type UsersStore = {
  ids: RefreshableContent<number[]>;
  active: number[];
  byId: Record<number, RefreshableEditableContent<User>>;
  byCompanyId: Record<number, number[]>;
  byMemberType: Record<keyof MemberType, number[]>;
};

const EMPTY: UsersStore = {
  byId: {},
  ids: Empty(),
  active: [],
  byCompanyId: {},
  byMemberType: fromPairs(
    MEMBER_TYPES.map((x) => [x, []]),
  ) as unknown as Record<keyof MemberType, number[]>,
};

const putAll = (users: User[]) => (): UsersStore => {
  const ids = users.map(({ userId }) => userId);
  const lookup = keyBy(users, 'userId') as Record<number, User>;

  return {
    byId: fromPairs(ids.map((id) => [id, Loaded(lookup[id])])),
    ids: Loaded(ids),
    active: ids.filter((id) => lookup[id].userStatus !== UserStatus.Inactive),
    byCompanyId: groupBy(ids, (id) => lookup[id].company.companyId),
    byMemberType: groupBy(ids, (id) => lookup[id].userType) as any,
  };
};

const added =
  (user: User) =>
  ({
    byId,
    ids,
    active,
    byCompanyId,
    byMemberType,
  }: UsersStore): UsersStore => {
    const [, userIds = []] = ids;

    return {
      byId: { ...byId, [user.userId]: Loaded(user) },
      ids: Loaded(union(userIds, [user.userId])),
      active: union(
        active,
        [user.userId].filter(() => user.userStatus !== UserStatus.Inactive),
      ),
      byCompanyId: {
        ...byCompanyId,
        [user.company.companyId]: union(
          byCompanyId[user.company.companyId] ?? [],
          [user.userId],
        ),
      },
      byMemberType: {
        ...byMemberType,
        [user.userType]: union(byMemberType[user.userType] ?? [], [
          user.userId,
        ]),
      },
    };
  };

const addedAll = (users: User[]) => (store: UsersStore) =>
  users.reduce((state, user) => added(user)(state), store);

const loaded = ({ ids: [status] }: UsersStore) =>
  status === 'loaded' || status === 'refreshing';

const toList = ({ ids: [, userIds = []], byId }: UsersStore): User[] =>
  userIds
    .map((x) => byId[x])
    .filter(Boolean)
    .filter(([status]) => status === 'loaded' || status === 'refreshing')
    .map(([, user]) => user!);

// TODO: Strongly type
const ofCompanyId =
  ([, companyId]: CompanyId) =>
  ({ byCompanyId }: UsersStore) =>
    byCompanyId[companyId] ?? [];
const activeUsers = ({ active: active_list, byId }: UsersStore) =>
  active_list
    .map((x) => byId[x])
    .filter(Boolean)
    .filter(([status]) => status === 'loaded' || status === 'refreshing')
    .map(([, user]) => user!);
const ofMemberType =
  (type: MemberType) =>
  ({ byMemberType }: UsersStore): number[] =>
    byMemberType[type] ?? [];

export const UsersStore = {
  empty: () => EMPTY,
  putAll,
  loaded,
  toList,
  ofCompanyId,
  activeUsers,
  ofMemberType,
  added,
  addedAll,
};
